import { useMemo, useState } from 'react';
import { computeBillTotals, computeDiscount, unitPriceFor } from '@sushimai/core';
import { api, newOpId } from '../lib/api.ts';
import { baht } from '../lib/format.ts';
import { printBillSlip, printKitchenSlips } from '../actions.ts';
import { useBill } from '../bill.tsx';
import { useStore } from '../store.tsx';
import { AsyncButton, Field, Modal, useToast } from '../ui.tsx';
import { MemberModal } from './MemberModal.tsx';
import type { SaleDetail } from '../lib/types.ts';

/**
 * ตะกร้า — คอลัมน์ขวาของหน้าขาย
 *
 * ★ ลอกจาก components/Cart.js ★
 *   · กว้าง 400px พื้น #DDDDDD
 *   · แบ่งเป็น 3 ช่วง: รายการก่อนหน้า / รายการยกเลิก / รายการใหม่
 *   · แต่ละบรรทัด: ปุ่ม [x{จำนวน}] · ชื่อ (รหัส) *หมายเหตุ · ราคาชิดขวา
 *   · ★ แตะปุ่ม x{จำนวน} = เพิ่มอีก 1 ★ ท่าที่พนักงานใช้ตลอดเวลา
 *   · ปุ่มล่าง: [พิมพ์] บันทึก(6) บิล(2) ชำระ(2)
 *
 * ★ กติกาการเปิด-ปิดปุ่มลอกมาเป๊ะ ★
 *   มีของค้างยังไม่บันทึก → กดได้แค่ "บันทึก"
 *   บันทึกครบแล้ว        → กดได้แค่ "บิล" กับ "ชำระ"
 *   ทำให้พนักงานกดพลาดข้ามขั้นไม่ได้ เหมือนของเดิม
 *
 * ต่างจากของเดิมอยู่อย่างเดียว — ของเดิมปัดบรรทัดไปทางซ้ายเพื่อ ลบ/แก้ไข
 * บนเว็บการปัดชนกับการเลื่อนรายการจนลบผิดได้ง่าย จึงเปลี่ยนเป็น
 * "แตะที่ชื่อรายการ" เพื่อเปิดหน้าต่างแก้ไข ส่วนท่าแตะ x{จำนวน} ยังเหมือนเดิมทุกอย่าง
 */
export function CartPanel({ onPay, onSaved }: { onPay: () => void; onSaved: () => void }) {
  const store = useStore();
  const bill = useBill();
  const toast = useToast();

  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [showMember, setShowMember] = useState(false);
  const opId = useMemo(() => ({ current: newOpId() }), []);

  const sale = bill.sale;
  const billType = bill.payload.billType === '' ? 'dine_in' : bill.payload.billType;

  const oldLines = sale?.lines.filter((l) => !l.voided) ?? [];
  const cancelledLines = sale?.lines.filter((l) => l.voided) ?? [];

  /** ยอดของรายการใหม่ — ใช้สูตรชุดเดียวกับเซิร์ฟเวอร์ ตัวเลขจึงไม่คลาดกัน */
  const cartTotals = useMemo(
    () =>
      computeBillTotals(
        bill.cart.map((item) => ({
          productId: item.product.id,
          qty: item.qty,
          unitPriceSatang: unitPriceFor(item.product, billType),
        })),
      ),
    [bill.cart, billType],
  );

  const hasUnsaved = bill.cart.length > 0 || bill.pendingDiscount !== null;
  const saved = sale !== null;
  const canSave = hasUnsaved;
  const canBillOrPay = saved && !hasUnsaved && oldLines.length > 0;

  const itemCount =
    oldLines.reduce((n, l) => n + l.qty, 0) + bill.cart.reduce((n, i) => n + i.qty, 0);

  /**
   * ★ ส่วนลดเปอร์เซ็นต์ต้องคิดใหม่จากยอดรวมใหม่ ★
   *
   * สั่งเพิ่มแล้วยอดสินค้าโตขึ้น ส่วนลด 10% ก็ต้องโตตาม เซิร์ฟเวอร์คิดใหม่ให้
   * ถ้าหน้าจอเอายอดเดิมมาบวกยอดใหม่ตรง ๆ ตัวเลขบนจอจะไม่ตรงกับที่บันทึกจริง
   * แล้วแคชเชียร์จะบอกราคาลูกค้าผิดตั้งแต่ก่อนกดบันทึก
   *
   * (บั๊กเดิมกลับด้านกัน — ระบบเก่าคิดส่วนลดใหม่แล้วได้ 0 เสมอ กระทบ 111 บรรทัด)
   */
  const combinedSubtotal = (sale?.subtotalSatang ?? 0) + cartTotals.subtotalSatang;

  /** บิลที่ยังไม่บันทึก ส่วนลดสมาชิกยังไม่มีในเซิร์ฟเวอร์ — แสดงตัวอย่างให้เห็นก่อน */
  const previewMemberPct =
    sale === null && bill.payload.memberPct > 0 ? bill.payload.memberPct : 0;

  const discountPreview = useMemo(() => {
    const parts = [
      ...(sale?.discounts ?? []).map((d) =>
        d.percent !== null
          ? { kind: 'percent' as const, percent: d.percent }
          : { kind: 'fixed' as const, amountSatang: d.amountSatang },
      ),
      ...(previewMemberPct > 0
        ? [{ kind: 'percent' as const, percent: previewMemberPct }]
        : []),
      ...(bill.pendingDiscount
        ? [
            bill.pendingDiscount.kind === 'percent'
              ? { kind: 'percent' as const, percent: bill.pendingDiscount.percent! }
              : { kind: 'fixed' as const, amountSatang: bill.pendingDiscount.amountSatang! },
          ]
        : []),
    ];
    return parts.reduce((sum, d) => sum + computeDiscount(combinedSubtotal, d), 0);
  }, [sale?.discounts, bill.pendingDiscount, previewMemberPct, combinedSubtotal]);

  const grandTotal = Math.max(0, combinedSubtotal - discountPreview);

  /** รหัสสินค้าจากรหัสภายใน — บรรทัดที่ส่งครัวแล้วเก็บแต่ชื่อ ไม่ได้เก็บรหัส */
  const codeOf = (productId: number) =>
    store.products.find((p) => p.id === productId)?.code ?? productId;

  /* ── บันทึก ─────────────────────────────────────────────── */

  const save = async () => {
    const items = bill.cart.map((item) => ({
      productId: item.product.id,
      qty: item.qty,
      note: item.note,
    }));

    try {
      let saleId = bill.payload.saleId;
      let round = 1;

      if (saleId === null) {
        if (!store.drawer) throw new Error('รอบลิ้นชักยังไม่เปิด');
        if (items.length === 0) throw new Error('ยังไม่มีรายการอาหาร');

        const created = await api.post<{ id: number; kitchenRound: number }>('/sales', {
          drawerSessionId: store.drawer.id,
          billType,
          diningTableId: billType === 'dine_in' ? bill.payload.diningTableId : null,
          guestCount: billType === 'dine_in' ? bill.payload.guestCount : 0,
          customerId: bill.payload.customerId,
          deliveryChannelId: billType === 'delivery' ? bill.payload.deliveryChannelId : null,
          deliveryOrderRef: bill.payload.deliveryOrderRef,
          items,
          clientOpId: opId.current,
        });
        saleId = created.id;
        round = created.kitchenRound;
      } else if (items.length > 0) {
        const added = await api.post<{ kitchenRound: number }>(`/sales/${saleId}/lines`, {
          items,
          clientOpId: opId.current,
        });
        round = added.kitchenRound;
      }

      // ★ ส่วนลดที่กดไว้จากกริดถูกส่งเป็นส่วนลดท้ายบิลจริง ★
      //   ไม่ใช่บรรทัดสินค้าราคาติดลบแบบระบบเดิม
      if (bill.pendingDiscount) {
        const d = bill.pendingDiscount;
        await api.post(`/sales/${saleId}/discount`,
          d.kind === 'percent'
            ? { kind: 'percent', percent: d.percent, label: d.label }
            : { kind: 'fixed', amountSatang: d.amountSatang, label: d.label },
        );
      }

      opId.current = newOpId();

      const fresh = await api.get<SaleDetail>(`/sales/${saleId}`);
      if (items.length > 0) {
        // ★ พิมพ์ไม่ออกไม่ทำให้ออเดอร์หาย ★ บันทึกไปแล้ว เตือนแล้วไปต่อ
        const problems = await printKitchenSlips(store, fresh, round);
        if (problems.length > 0) {
          toast(`ใบครัวพิมพ์ไม่ออก — ${problems.join(' · ')}`, 'crit');
        }
      }

      // ของเดิมบันทึกเสร็จแล้วเด้งกลับหน้ารายการบิลทันที
      bill.reset();
      onSaved();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'บันทึกไม่สำเร็จ', 'crit');
    }
  };

  /* ── บิล (พิมพ์ใบแจ้งยอด) ───────────────────────────────── */

  const printCheck = async () => {
    if (!sale) return;
    const problem = await printBillSlip(store, sale, true);
    toast(problem ?? 'พิมพ์ใบแจ้งยอดแล้ว', problem ? 'crit' : 'ok');
  };

  const reprintKitchen = async () => {
    if (!sale) return;
    const rounds = [...new Set(sale.lines.map((l) => l.orderRound))];
    const last = Math.max(...rounds);
    const problems = await printKitchenSlips(store, sale, last);
    toast(problems.length ? problems.join(' · ') : `พิมพ์ใบครัวรอบ ${last} ซ้ำแล้ว`,
          problems.length ? 'crit' : 'ok');
  };

  /* ── ยกเลิกรายการที่ส่งครัวไปแล้ว ────────────────────────── */

  const voidLine = async (lineId: number, reason: string) => {
    try {
      await api.post(`/sales/${sale!.id}/lines/${lineId}/void`, { reason });
      await bill.refresh();
      toast('ยกเลิกรายการแล้ว', 'warn');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'ยกเลิกไม่สำเร็จ', 'crit');
    }
  };

  const editing = editIndex !== null ? bill.cart[editIndex] : null;

  return (
    <aside className="cart">
      <div className="cart-scroll">
        {oldLines.length > 0 && <div className="cart-divider">รายการก่อนหน้า</div>}
        {oldLines.map((line) => (
          <OldRow
            key={line.id}
            /* ★ แสดงรหัสสินค้าต่อท้ายชื่อเหมือนของเดิม ★ พนักงานใช้รหัสอ้างอิงกับครัว */
            name={`${line.productName} (${codeOf(line.productId)})`}
            note={line.note}
            qty={line.qty}
            amount={line.lineTotalSatang}
            onVoid={(reason) => voidLine(line.id, reason)}
          />
        ))}

        {cancelledLines.length > 0 && <div className="cart-divider">รายการยกเลิก</div>}
        {cancelledLines.map((line) => (
          <div key={line.id} className="cart-row cancelled">
            <span className="qty" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              x{line.qty}
            </span>
            <span className="cname">
              {line.productName}
              {line.voidReason ? ` — ${line.voidReason}` : ''}
            </span>
            <span className="camt">{baht(line.lineTotalSatang)}</span>
          </div>
        ))}

        {bill.cart.length > 0 && <div className="cart-divider">รายการใหม่</div>}
        {bill.cart.map((item, index) => (
          <div key={`${item.product.id}-${index}`} className="cart-row">
            {/* ★ แตะปุ่มนี้ = เพิ่มอีก 1 ★ ท่าเดียวกับของเดิม */}
            <button className="qty" onClick={() => bill.setQty(index, item.qty + 1)}>
              x{item.qty}
            </button>
            <button
              className="cname"
              style={{ textAlign: 'left' }}
              onClick={() => setEditIndex(index)}
            >
              {item.product.name} ({item.product.code}){item.note ? ` *${item.note}` : ''}
            </button>
            <span className="camt">
              {baht(unitPriceFor(item.product, billType) * item.qty)}
            </span>
          </div>
        ))}

        {!saved && bill.cart.length === 0 && (
          <div className="empty">แตะเมนูทางซ้ายเพื่อเริ่มสั่ง</div>
        )}
      </div>

      <div className="cart-foot">
        {/* ส่วนลดอยู่ล่างสุดเหนือปุ่ม เหมือนของเดิม */}
        {sale?.discounts.map((d) => (
          <div key={d.id} className="cart-row discount">
            <span style={{ width: 40 }} />
            <span className="cname">{d.label}</span>
            {/* คิดใหม่จากยอดรวมปัจจุบัน ไม่ใช่ยอดที่บันทึกไว้ตอนก่อนสั่งเพิ่ม */}
            <span className="camt">
              −{baht(d.percent !== null
                  ? computeDiscount(combinedSubtotal, { kind: 'percent', percent: d.percent })
                  : d.amountSatang)}
            </span>
          </div>
        ))}
        {previewMemberPct > 0 && (
          <div className="cart-row discount">
            <span style={{ width: 40 }} />
            <span className="cname">ส่วนลดสมาชิก {previewMemberPct}%</span>
            <span className="camt">
              −{baht(computeDiscount(combinedSubtotal, { kind: 'percent', percent: previewMemberPct }))}
            </span>
          </div>
        )}
        {bill.pendingDiscount && (
          <div className="cart-row discount">
            <span style={{ width: 40 }} />
            <span className="cname">{bill.pendingDiscount.label} (ยังไม่บันทึก)</span>
            <button className="edit" onClick={bill.clearPendingDiscount}>✕</button>
          </div>
        )}

        {bill.payload.customerId !== null && (
          <div className="cart-member">
            <span style={{ flex: 1 }}>
              สมาชิก : {bill.payload.customerName} {bill.payload.customerPhone}
            </span>
            <button
              className="edit"
              onClick={async () => {
                const saleId = bill.payload.saleId;
                bill.setPayload({
                  customerId: null, customerName: '', customerPhone: '', memberPct: 0,
                });
                // ถอดสมาชิกออกจากบิลที่บันทึกแล้ว ต้องเอาส่วนลดออกที่เซิร์ฟเวอร์ด้วย
                if (saleId !== null) {
                  try {
                    await api.put(`/sales/${saleId}`, { customerId: null });
                    await bill.refresh();
                  } catch (err) {
                    toast(err instanceof Error ? err.message : 'ถอดสมาชิกไม่สำเร็จ', 'crit');
                  }
                }
              }}
            >
              ✕
            </button>
          </div>
        )}

        {bill.payload.billType !== '' && (
          <>
            <div className="cart-tools">
              <button className="cart-tool" onClick={() => setShowMember(true)}>
                <span>👤</span> สมาชิก
              </button>
              <button
                className="cart-tool"
                onClick={() => {
                  bill.cart.forEach(() => bill.removeItem(0));
                  bill.clearPendingDiscount();
                }}
              >
                <span>🗑</span> ล้างรายการ
              </button>
            </div>

            <div className="cart-total">
              <span className="grow">ทั้งหมด ({itemCount} รายการ)</span>
              <span>{baht(grandTotal)}</span>
            </div>

            <div className="cart-actions">
              {saved && (
                <button className="print" onClick={() => void reprintKitchen()} title="พิมพ์ใบครัวซ้ำ">
                  🖨
                </button>
              )}
              <AsyncButton className="act save" onClick={save} disabled={!canSave}>
                บันทึก
              </AsyncButton>
              <AsyncButton className="act bill" onClick={printCheck} disabled={!canBillOrPay}>
                บิล
              </AsyncButton>
              <button className="act pay" disabled={!canBillOrPay} onClick={onPay}>
                ชำระ
              </button>
            </div>
          </>
        )}
      </div>

      {editing && editIndex !== null && (
        <Modal title={editing.product.name} onClose={() => setEditIndex(null)}>
          <Field label="จำนวน">
            <div className="qty-row">
              <button className="qty-btn" onClick={() => bill.setQty(editIndex, editing.qty - 1)}>−</button>
              <span className="qty-value">{editing.qty}</span>
              <button className="qty-btn" onClick={() => bill.setQty(editIndex, editing.qty + 1)}>+</button>
            </div>
          </Field>
          <Field label="หมายเหตุถึงครัว">
            <input
              value={editing.note}
              onChange={(e) => bill.setNote(editIndex, e.target.value)}
              placeholder="เช่น *ไม่ใส่ผัก, *เผ็ดน้อย"
            />
          </Field>
          <div className="action-row">
            <button
              className="btn danger"
              onClick={() => {
                bill.removeItem(editIndex);
                setEditIndex(null);
              }}
            >
              ลบรายการ
            </button>
            <button className="btn" onClick={() => setEditIndex(null)}>เสร็จ</button>
          </div>
        </Modal>
      )}

      {showMember && (
        <MemberModal
          onClose={() => setShowMember(false)}
          onPicked={async (customer) => {
            bill.setPayload({
              customerId: customer.id,
              customerName: customer.name,
              customerPhone: customer.phone,
              memberPct: customer.memberDiscountPct,
            });
            setShowMember(false);

            /**
             * ★ บิลที่บันทึกไปแล้วต้องส่งไปผูกที่เซิร์ฟเวอร์ทันที ★
             * ไม่งั้นส่วนลดจะไม่ขึ้นจนกว่าจะกดบันทึกอีกครั้ง ซึ่งพนักงานไม่มีเหตุให้กด
             * (นี่คือบั๊กที่เจอตอนทดสอบบนเครื่องจริง)
             */
            if (bill.payload.saleId !== null) {
              try {
                await api.put(`/sales/${bill.payload.saleId}`, { customerId: customer.id });
                await bill.refresh();
                if (customer.memberDiscountPct > 0) {
                  toast(`ใส่ส่วนลดสมาชิก ${customer.memberDiscountPct}% แล้ว`, 'ok');
                } else {
                  toast(`${customer.name} ไม่มีส่วนลดสมาชิก`, 'warn');
                }
              } catch (err) {
                toast(err instanceof Error ? err.message : 'ผูกสมาชิกไม่สำเร็จ', 'crit');
              }
            } else if (customer.memberDiscountPct <= 0) {
              toast(`${customer.name} ไม่มีส่วนลดสมาชิก`, 'warn');
            }
          }}
        />
      )}
    </aside>
  );
}

/** บรรทัดที่ส่งครัวไปแล้ว — แก้จำนวนไม่ได้ ทำได้แค่ยกเลิกพร้อมเหตุผล */
function OldRow({
  name,
  note,
  qty,
  amount,
  onVoid,
}: {
  name: string;
  note: string;
  qty: number;
  amount: number;
  onVoid: (reason: string) => Promise<void>;
}) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');

  return (
    <>
      <div className="cart-row">
        <span className="qty" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          x{qty}
        </span>
        <button className="cname" style={{ textAlign: 'left' }} onClick={() => setAsking(true)}>
          {name}{note ? ` *${note}` : ''}
        </button>
        <span className="camt">{baht(amount)}</span>
      </div>

      {asking && (
        <Modal title="ยกเลิกรายการ" onClose={() => setAsking(false)}>
          <p className="lede">
            {name} — รายการนี้ส่งครัวไปแล้ว ยกเลิกได้แต่ต้องกรอกเหตุผล
            และระบบจะแจ้งเจ้าของร้าน
          </p>
          <Field label="เหตุผล">
            <input value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
          </Field>
          <div className="action-row">
            <button className="btn ghost" onClick={() => setAsking(false)}>ไม่ยกเลิก</button>
            <button
              className="btn danger"
              disabled={reason.trim() === ''}
              onClick={async () => {
                await onVoid(reason.trim());
                setAsking(false);
              }}
            >
              ยืนยันยกเลิก
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
